/**
 * Currency Changer Utility
 * 
 * A plain JavaScript utility that finds currency text in the DOM
 * and converts amounts on the fly using static exchange rates.
 * 
 * Default currency: EUR
 * 
 * Usage:
 *   import { changeCurrency, initCurrencyChanger } from './utils/currencyChanger';
 *   
 *   // Change all prices on the page to USD
 *   changeCurrency('USD');
 *   
 *   // Reset to default (EUR)
 *   changeCurrency('EUR');
 */

// The two rates that are configured per deployment:
//   GATSBY_EUR_TO_BDT - BDT per 1 EUR
//   GATSBY_USD_TO_BDT - BDT per 1 USD
//
// EUR->USD is DERIVED from that pair (EUR_TO_BDT / USD_TO_BDT) instead of being
// configured on its own, so the three rates can never contradict each other:
// going EUR->USD->BDT always lands on the same figure as EUR->BDT directly.
//
// The GATSBY_ prefix is required, not stylistic - Gatsby only inlines env vars
// into the browser bundle when they carry it, and every conversion runs
// client-side. The defaults below are the fallback when the vars are unset.
const DEFAULT_EUR_TO_BDT = 142;
const DEFAULT_USD_TO_BDT = 122;

function readConfiguredRate(rawValue, fallback) {
  const rate = Number.parseFloat(rawValue);
  return Number.isFinite(rate) && rate > 0 ? rate : fallback;
}

const EUR_TO_BDT = readConfiguredRate(
  process.env.GATSBY_EUR_TO_BDT,
  DEFAULT_EUR_TO_BDT
);
const USD_TO_BDT = readConfiguredRate(
  process.env.GATSBY_USD_TO_BDT,
  DEFAULT_USD_TO_BDT
);

// Static exchange rates (base: EUR)
// EUR to other currencies conversion rates
const EXCHANGE_RATES = {
  EUR: 1,           // Base currency
  USD: EUR_TO_BDT / USD_TO_BDT, // derived, e.g. 142 / 122 = 1.1639
  GBP: 0.88,        // 1 EUR = 0.86 GBP
  BDT: EUR_TO_BDT,  // from GATSBY_EUR_TO_BDT
  INR: 105.90,      // 1 EUR = 105.90 INR
  AUD: 1.61,        // 1 EUR = 1.61 AUD
  CAD: 1.62,        // 1 EUR = 1.62 CAD
  JPY: 182.30,      // 1 EUR = 182.30 JPY
  CNY: 8.28,        // 1 EUR = 8.28 CNY
  AED: 3.67,        // 1 EUR = 3.67 AED
  SGD: 1.29,        // 1 EUR = 1.29 SGD
  MYR: 4.70,        // 1 EUR = 4.70 MYR
  PKR: 303.50,      // 1 EUR = 303.50 PKR
  SAR: 3.77,        // 1 EUR = 3.77 SAR
  QAR: 3.75,        // 1 EUR = 3.75 QAR
  KWD: 0.34,        // 1 EUR = 0.34 KWD
  OMR: 0.42,        // 1 EUR = 0.42 OMR
  BHD: 0.41,        // 1 EUR = 0.41 BHD
  CHF: 0.94,        // 1 EUR = 0.94 CHF
  
};

// Currency symbols and configurations
// Every currency formats to two decimals. Whole-unit rounding destroyed
// sub-unit amounts (an hourly rate of BDT 1.70 rendered as BDT 2), and the
// detection regexes only parse up to two decimals anyway, so a 3-decimal
// currency could emit a value it could not read back.
const CURRENCY_CONFIG = {
  EUR: { symbol: '€', position: 'before', decimals: 2, name: 'Euro', code: 'EUR' },
  USD: { symbol: '$', position: 'before', decimals: 2, name: 'US Dollar', code: 'USD' },
  GBP: { symbol: '£', position: 'before', decimals: 2, name: 'British Pound', code: 'GBP' },
  BDT: { symbol: '৳', position: 'before', decimals: 2, name: 'Bangladeshi Taka', code: 'BDT' },
  INR: { symbol: '₹', position: 'before', decimals: 2, name: 'Indian Rupee', code: 'INR' },
  AUD: { symbol: 'A$', position: 'before', decimals: 2, name: 'Australian Dollar', code: 'AUD' },
  CAD: { symbol: 'C$', position: 'before', decimals: 2, name: 'Canadian Dollar', code: 'CAD' },
  JPY: { symbol: '¥', position: 'before', decimals: 2, name: 'Japanese Yen', code: 'JPY' },
  CNY: { symbol: '¥', position: 'before', decimals: 2, name: 'Chinese Yuan', code: 'CNY' },
  AED: { symbol: 'د.إ', position: 'after', decimals: 2, name: 'UAE Dirham', code: 'AED' },
  SGD: { symbol: 'S$', position: 'before', decimals: 2, name: 'Singapore Dollar', code: 'SGD' },
  MYR: { symbol: 'RM', position: 'before', decimals: 2, name: 'Malaysian Ringgit', code: 'MYR' },
  PKR: { symbol: '₨', position: 'before', decimals: 2, name: 'Pakistani Rupee', code: 'PKR' },
  SAR: { symbol: '﷼', position: 'after', decimals: 2, name: 'Saudi Riyal', code: 'SAR' },
  QAR: { symbol: '﷼', position: 'after', decimals: 2, name: 'Qatari Riyal', code: 'QAR' },
  KWD: { symbol: 'د.ك', position: 'after', decimals: 2, name: 'Kuwaiti Dinar', code: 'KWD' },
  OMR: { symbol: '﷼', position: 'after', decimals: 2, name: 'Omani Rial', code: 'OMR' },
  BHD: { symbol: 'BD', position: 'before', decimals: 2, name: 'Bahraini Dinar', code: 'BHD' },
  CHF: { symbol: 'CHF', position: 'before', decimals: 2, name: 'Swiss Franc', code: 'CHF' },
};

// Countries whose local currency is supported by this utility.
const COUNTRY_CURRENCY_MAP = {
  AD: 'EUR', AT: 'EUR', BE: 'EUR', BL: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR',
  ES: 'EUR', FI: 'EUR', FR: 'EUR', GF: 'EUR', GP: 'EUR', GR: 'EUR', HR: 'EUR',
  IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR', LV: 'EUR', MC: 'EUR', ME: 'EUR',
  MF: 'EUR', MQ: 'EUR', MT: 'EUR', NL: 'EUR', PM: 'EUR', PT: 'EUR', RE: 'EUR',
  SI: 'EUR', SK: 'EUR', SM: 'EUR', VA: 'EUR', XK: 'EUR', YT: 'EUR',
  US: 'USD', AS: 'USD', BQ: 'USD', EC: 'USD', FM: 'USD', GU: 'USD', MH: 'USD',
  MP: 'USD', PA: 'USD', PR: 'USD', PW: 'USD', SV: 'USD', TL: 'USD', VI: 'USD',
  GB: 'GBP', GG: 'GBP', IM: 'GBP', JE: 'GBP',
  BD: 'BDT', IN: 'INR', AU: 'AUD', CA: 'CAD', JP: 'JPY', CN: 'CNY', AE: 'AED',
  SG: 'SGD', MY: 'MYR', PK: 'PKR', SA: 'SAR', QA: 'QAR', KW: 'KWD', OM: 'OMR',
  BH: 'BHD', CH: 'CHF', LI: 'CHF',
};

// Timezone is checked before locale because it usually reflects the user's
// current system location even when their browser language is set to English.
const TIME_ZONE_CURRENCY_MAP = {
  'Asia/Dhaka': 'BDT',
  'Asia/Dacca': 'BDT',
  'Asia/Calcutta': 'INR',
  'Asia/Kolkata': 'INR',
  'Asia/Tokyo': 'JPY',
  'Asia/Shanghai': 'CNY',
  'Asia/Urumqi': 'CNY',
  'Asia/Dubai': 'AED',
  'Asia/Singapore': 'SGD',
  'Asia/Kuala_Lumpur': 'MYR',
  'Asia/Kuching': 'MYR',
  'Asia/Karachi': 'PKR',
  'Asia/Riyadh': 'SAR',
  'Asia/Qatar': 'QAR',
  'Asia/Kuwait': 'KWD',
  'Asia/Muscat': 'OMR',
  'Asia/Bahrain': 'BHD',
  'Europe/London': 'GBP',
  'Europe/Belfast': 'GBP',
  'Europe/Guernsey': 'GBP',
  'Europe/Isle_of_Man': 'GBP',
  'Europe/Jersey': 'GBP',
  'Europe/Zurich': 'CHF',
  'Europe/Vaduz': 'CHF',
  'America/Toronto': 'CAD',
  'America/Vancouver': 'CAD',
  'America/Edmonton': 'CAD',
  'America/Winnipeg': 'CAD',
  'America/Halifax': 'CAD',
  'America/Moncton': 'CAD',
  'America/Regina': 'CAD',
  'America/St_Johns': 'CAD',
  'America/Whitehorse': 'CAD',
  'America/Yellowknife': 'CAD',
  'America/New_York': 'USD',
  'America/Chicago': 'USD',
  'America/Denver': 'USD',
  'America/Los_Angeles': 'USD',
  'America/Phoenix': 'USD',
  'America/Anchorage': 'USD',
  'Pacific/Honolulu': 'USD',
};

// Default/base currency
const DEFAULT_CURRENCY = 'EUR';

// Regex patterns for detecting currency in text
// These patterns detect various currency formats with optional spaces
const CURRENCY_PATTERNS = [
  // Euro: €35, € 35, €35.00, € 35.00, €1,234.56
  { regex: /€\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'EUR' },
  // Dollar: $100, $ 100, $100.00, $ 100.00, $1,234.56
  { regex: /\$\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'USD' },
  // Pound: £100, £ 100, £100.00, £1,234.56
  { regex: /£\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'GBP' },
  // Bangladeshi Taka: ৳100, ৳ 100
  { regex: /৳\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'BDT' },
  // Indian Rupee: ₹100, ₹ 100
  { regex: /₹\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'INR' },
  // Yen: ¥100, ¥ 100
  { regex: /¥\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'JPY' },
  // Letter-based markers (A$, C$, S$, RM, CHF and the ISO codes below) must be
  // anchored with \b, or they match INSIDE ordinary words and convert numbers
  // that were never prices: "PLATFORM 2024" contains "RM 2024", "MASSEUR 5"
  // contains "EUR 5", "500 European" contains "500 EUR", and "US$ 16.99"
  // contains "S$ 16.99". The symbol patterns above need no anchor because
  // €/$/£/৳/₹/¥ cannot occur inside a word.
  // Australian Dollar: A$100, A$ 100
  { regex: /\bA\$\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'AUD' },
  // Canadian Dollar: C$100, C$ 100
  { regex: /\bC\$\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'CAD' },
  // Singapore Dollar: S$100, S$ 100
  { regex: /\bS\$\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'SGD' },
  // Malaysian Ringgit: RM100, RM 100
  { regex: /\bRM\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'MYR' },
  // Swiss Franc: CHF100, CHF 100
  { regex: /\bCHF\s*([\d,]+(?:\.\d{1,2})?)/g, currency: 'CHF' },
  // Euro AFTER number (European format): 35 €, 0,99 €, 4,99 €, 1.234,56 €
  { regex: /((?:\d+\.)*\d+(?:,\d{1,2})?)\s*€/g, currency: 'EUR', european: true },
  // Currency code formats: EUR 100, EUR100, 100 EUR
  // The \b sits on the outer edge of the code so "EUR100"/"100EUR" still match.
  { regex: /\bEUR\s*([\d,]+(?:\.\d{1,2})?)/gi, currency: 'EUR' },
  { regex: /([\d,]+(?:\.\d{1,2})?)\s*EUR\b/gi, currency: 'EUR' },
  { regex: /\bUSD\s*([\d,]+(?:\.\d{1,2})?)/gi, currency: 'USD' },
  { regex: /([\d,]+(?:\.\d{1,2})?)\s*USD\b/gi, currency: 'USD' },
  { regex: /\bGBP\s*([\d,]+(?:\.\d{1,2})?)/gi, currency: 'GBP' },
  { regex: /([\d,]+(?:\.\d{1,2})?)\s*GBP\b/gi, currency: 'GBP' },
  { regex: /\bBDT\s*([\d,]+(?:\.\d{1,2})?)/gi, currency: 'BDT' },
  { regex: /([\d,]+(?:\.\d{1,2})?)\s*BDT\b/gi, currency: 'BDT' },
];

// Currency symbols for detection
const CURRENCY_SYMBOLS = {
  '€': 'EUR',
  '$': 'USD',
  '£': 'GBP',
  '৳': 'BDT',
  '₹': 'INR',
  '¥': 'JPY',
};

// Store original values for reverting
let originalValues = new Map();
// Store split price pairs (symbol element + amount element)
let splitPricePairs = [];
// Store amount-before-symbol pairs (<span>0,99</span> €/month)
let amountSymbolPairs = [];
let currentCurrency = DEFAULT_CURRENCY;
let isInitialized = false;
let hasAppliedCurrency = false;
let currencyMutationObserver = null;

function observeCurrencyMutations() {
  if (currencyMutationObserver && typeof document !== 'undefined' && document.body) {
    currencyMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

/**
 * Parse a currency string to extract the numeric value
 * @param {string} str - The string containing the amount (e.g., "1,234.56")
 * @returns {number} - The parsed numeric value
 */
function parseAmount(str) {
  return parseFloat(str.replace(/,/g, ''));
}

// Parses European decimal format where comma is the decimal separator.
// "0,99" → 0.99, "1.234,56" → 1234.56, "1.234" → 1234 (period = thousands)
function parseEuropeanAmount(str) {
  if (/,\d{1,2}$/.test(str)) {
    // comma is decimal separator: strip period thousands, swap comma→period
    return parseFloat(str.replace(/\./g, '').replace(',', '.'));
  }
  if (/\d\.\d{3}/.test(str)) {
    // period is thousands separator (e.g. 1.234)
    return parseFloat(str.replace(/\./g, ''));
  }
  return parseFloat(str.replace(/,/g, ''));
}

/**
 * Format a number as currency
 * @param {number} amount - The numeric amount
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @returns {string} - Formatted currency string with trailing space
 */
function formatCurrency(amount, currencyCode) {
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.EUR;
  const decimals = config.decimals;
  
  // Format the number with appropriate decimal places and thousand separators
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  // Position the symbol and add trailing space after the formatted price
  if (config.position === 'before') {
    return `${config.symbol}${formattedAmount} `;
  }
  return `${formattedAmount}${config.symbol} `;
}

/**
 * Convert an amount from one currency to another
 * All conversions go through EUR as base currency
 * 
 * @param {number} amount - The amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {number} - Converted amount
 */
function convertAmount(amount, fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return amount;
  
  const fromRate = EXCHANGE_RATES[fromCurrency] || 1;
  const toRate = EXCHANGE_RATES[toCurrency] || 1;
  
  // Convert to EUR first (base currency), then to target currency
  const amountInEUR = amount / fromRate;
  return amountInEUR * toRate;
}

/**
 * Process a text node to find and replace currency values
 * @param {Text} textNode - The text node to process
 * @param {string} targetCurrency - The currency to convert to
 */
function processTextNode(textNode, targetCurrency) {
  const originalText = textNode.textContent;

  // Store original if not already stored
  if (!originalValues.has(textNode)) {
    originalValues.set(textNode, originalText);
  }

  // Collect matches from every pattern against the ORIGINAL text. Replacing
  // one pattern at a time re-scans text this function just rewrote, so an
  // "A$18.13" produced by the AUD pass is matched again by the "$" (USD)
  // pattern and converted a second time ("AA$24.95").
  const matches = [];
  for (const pattern of CURRENCY_PATTERNS) {
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
    let match;

    while ((match = regex.exec(originalText)) !== null) {
      const amount = pattern.european
        ? parseEuropeanAmount(match[1])
        : parseAmount(match[1]);

      if (!isNaN(amount)) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          amount,
          currency: pattern.currency,
        });
      }

      // Guard against a zero-length match spinning the loop forever
      if (match[0] === '') regex.lastIndex++;
    }
  }

  if (matches.length === 0) return;

  // Earliest match wins; on a tie the longest one does, so "A$10" is read as
  // AUD rather than as the "$" pattern that starts one character later.
  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

  let text = '';
  let cursor = 0;
  for (const item of matches) {
    // Skip anything overlapping a match that was already applied
    if (item.start < cursor) continue;
    text += originalText.slice(cursor, item.start);
    text += formatCurrency(
      convertAmount(item.amount, item.currency, targetCurrency),
      targetCurrency
    );
    cursor = item.end;
  }
  text += originalText.slice(cursor);

  // Only update if text changed
  if (text !== originalText) {
    textNode.textContent = text;
  }
}

/**
 * Find and process split price patterns where symbol and amount are in separate elements
 * Pattern: <span>€</span><span>0.60</span>
 * @param {Element} root - The root element to search within
 * @param {string} targetCurrency - The currency to convert to
 */
function processSplitPrices(root, targetCurrency, elements) {
  // Find all elements that contain only a currency symbol (with optional whitespace)
  const allElements = elements || root.querySelectorAll('*');
  
  allElements.forEach((element) => {
    // Skip if in currency selector
    if (element.closest('#currency-selector')) return;
    
    const text = element.textContent?.trim();
    if (!text) return;
    
    // Check if this element contains only a currency symbol
    const symbolMatch = Object.keys(CURRENCY_SYMBOLS).find(symbol => text === symbol);
    
    if (symbolMatch) {
      // Check if this element has ONLY text content (no child elements with text)
      // This ensures we're looking at the actual symbol element
      const hasOnlyTextContent = (element.childNodes.length === 1 && element.childNodes[0].nodeType === Node.TEXT_NODE) ||
                                  (element.childNodes.length === 1 && element.childNodes[0].textContent?.trim() === symbolMatch);
      
      if (!hasOnlyTextContent && element.children.length > 0) return;
      
      // Look for the next sibling that contains a number
      let nextSibling = element.nextElementSibling;
      
      if (nextSibling) {
        const siblingText = nextSibling.textContent?.trim();
        // Check if sibling starts with a number (price amount)
        const amountMatch = siblingText?.match(/^([\d,]+(?:\.\d{1,2})?)/);
        
        if (amountMatch) {
          const originalCurrency = CURRENCY_SYMBOLS[symbolMatch];
          const amount = parseAmount(amountMatch[1]);
          
          if (!isNaN(amount)) {
            // Store original values for this pair
            const pairKey = `${element.outerHTML}-${nextSibling.outerHTML}`;
            const existingPair = splitPricePairs.find(p => p.key === pairKey);
            
            if (!existingPair) {
              splitPricePairs.push({
                key: pairKey,
                symbolElement: element,
                amountElement: nextSibling,
                originalSymbol: element.textContent,
                originalAmount: nextSibling.textContent,
                originalCurrency: originalCurrency,
              });
            }
            
            // Convert the amount
            const convertedAmount = convertAmount(amount, originalCurrency, targetCurrency);
            const targetConfig = CURRENCY_CONFIG[targetCurrency];
            const decimals = targetConfig.decimals;
            
            // Format amount only (no symbol)
            const formattedAmount = convertedAmount.toLocaleString('en-US', {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            });
            
            // Update symbol element
            // Preserve leading/trailing whitespace from original
            const originalSymbolText = element.textContent;
            const leadingSpace = originalSymbolText.match(/^(\s*)/)?.[1] || '';
            const trailingSpace = originalSymbolText.match(/(\s*)$/)?.[1] || '';
            element.textContent = `${leadingSpace}${targetConfig.symbol}${trailingSpace}`;
            
            // Update amount element - replace only the number part
            const originalAmountText = nextSibling.textContent;
            nextSibling.textContent = originalAmountText.replace(amountMatch[1], formattedAmount);
          }
        }
      }
    }
  });
}

/**
 * Restore split price pairs to original values
 */
function restoreSplitPrices() {
  splitPricePairs.forEach((pair) => {
    if (pair.symbolElement && pair.symbolElement.parentNode) {
      pair.symbolElement.textContent = pair.originalSymbol;
    }
    if (pair.amountElement && pair.amountElement.parentNode) {
      pair.amountElement.textContent = pair.originalAmount;
    }
  });
}

/**
 * Restore amount-before-symbol pairs to original values.
 * Only restores the amount element; the symbol text node is handled by originalValues.
 */
function restoreAmountBeforeSymbol() {
  amountSymbolPairs.forEach((pair) => {
    if (pair.amountElement?.parentNode && pair.amountElement.childNodes[0]) {
      pair.amountElement.childNodes[0].textContent = pair.originalAmount;
    }
  });
}

/**
 * Handle prices where the amount is in one element and the currency symbol
 * is in the immediately following text node: <span>0,99</span> €/month
 */
function processAmountBeforeSymbol(root, targetCurrency, elements) {
  if (!root.querySelectorAll) return;
  const targetConfig = CURRENCY_CONFIG[targetCurrency];
  const allElements = elements || root.querySelectorAll('*');

  allElements.forEach((element) => {
    if (element.closest('#currency-selector')) return;
    if (element.children.length > 0) return;

    const text = element.textContent?.trim();
    if (!text) return;

    // Element must contain only a numeric amount (European or standard format)
    const amountMatch = text.match(/^((?:\d+\.)*\d+(?:,\d{1,2})?)$/);
    if (!amountMatch) return;

    // Find the nearest following text node (skip element siblings)
    let nextSibling = element.nextSibling;
    while (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE) {
      nextSibling = nextSibling.nextSibling;
    }
    if (!nextSibling || nextSibling.nodeType !== Node.TEXT_NODE) return;

    const siblingText = nextSibling.textContent || '';

    // Text node must start with a known currency symbol
    const symbolMatch = siblingText.match(/^[\s ]*(€|\$|£|৳|₹|¥)/);
    if (!symbolMatch) return;

    const symbolChar = symbolMatch[1];
    const originalCurrency = CURRENCY_SYMBOLS[symbolChar];
    if (!originalCurrency) return;

    const amount = parseEuropeanAmount(amountMatch[1]);
    if (isNaN(amount)) return;

    // Track by element identity (not outerHTML) so navigation doesn't cause collisions
    const existingPair = amountSymbolPairs.find(p => p.amountElement === element);
    if (!existingPair) {
      amountSymbolPairs.push({
        amountElement: element,
        symbolTextNode: nextSibling,
        originalAmount: element.childNodes[0]?.textContent ?? text,
        originalCurrency,
      });
    }

    const convertedAmount = convertAmount(amount, originalCurrency, targetCurrency);
    const formattedAmount = convertedAmount.toLocaleString('en-US', {
      minimumFractionDigits: targetConfig.decimals,
      maximumFractionDigits: targetConfig.decimals,
    });

    // Modify the text node directly (characterData) to avoid triggering MutationObserver
    if (element.childNodes[0]) {
      element.childNodes[0].textContent = formattedAmount;
    }
    // Swap just the symbol in the following text node
    nextSibling.textContent = siblingText.replace(symbolChar, targetConfig.symbol);
  });
}

/**
 * Walk through all text nodes in the DOM and process them
 * @param {Element} root - The root element to start from
 * @param {string} targetCurrency - The currency to convert to
 */
function walkDOM(root, targetCurrency) {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        // Skip script, style, and noscript tags
        const parent = node.parentElement;
        if (parent && (
          parent.tagName === 'SCRIPT' || 
          parent.tagName === 'STYLE' || 
          parent.tagName === 'NOSCRIPT' ||
          parent.closest('#currency-selector') // Skip currency selector itself
        )) {
          return NodeFilter.FILTER_REJECT;
        }
        // Only process nodes with potential currency content
        if (node.textContent && /[$€£₹৳¥]|EUR|USD|GBP|BDT|INR|JPY|CHF|A\$|C\$|S\$|RM/i.test(node.textContent)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      }
    }
  );
  
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    textNodes.push(node);
  }
  
  // Process all found text nodes
  textNodes.forEach(textNode => processTextNode(textNode, targetCurrency));
}

function processCurrencyRoot(root, targetCurrency) {
  if (!root?.querySelectorAll) return;

  // Reuse one element collection for both structured-price passes instead of
  // scanning the same subtree repeatedly.
  const elements = root.querySelectorAll('*');
  walkDOM(root, targetCurrency);
  processSplitPrices(root, targetCurrency, elements);
  processAmountBeforeSymbol(root, targetCurrency, elements);
}

export function markCurrencyReady(currency = currentCurrency) {
  if (typeof document === 'undefined') return;

  document.documentElement.setAttribute('data-currency', currency);
  document.documentElement.classList.remove('currency-pending');
  if (typeof window !== 'undefined') {
    window.__INITIAL_CURRENCY__ = currency;
    window.__CURRENCY_READY__ = true;
    if (window.__CURRENCY_REVEAL_TIMER__) {
      clearTimeout(window.__CURRENCY_REVEAL_TIMER__);
      window.__CURRENCY_REVEAL_TIMER__ = null;
    }
  }
}

/**
 * Change all currency values on the page to the target currency
 * @param {string} targetCurrency - The currency code to convert to (e.g., 'EUR', 'USD')
 * @param {Element} [root=document.body] - The root element to search within
 */
export function changeCurrency(targetCurrency, root) {
  if (!root) {
    if (typeof document === 'undefined') return;
    root = document.body;
  }
  if (!CURRENCY_CONFIG[targetCurrency]) {
    console.warn(`Currency ${targetCurrency} not supported. Using EUR.`);
    targetCurrency = DEFAULT_CURRENCY;
  }

  // Avoid observing the DOM writes performed by this conversion. Without
  // this pause, split-price updates can schedule conversion again every frame.
  currencyMutationObserver?.disconnect();

  // The bootstrap style hides the currency selector until markCurrencyReady(), so every
  // exit from this function - including a throw part-way through the DOM walk -
  // has to reveal the selector, even if conversion fails.
  try {
    // Reset to original values first if we have stored originals
    if (originalValues.size > 0) {
      const staleNodes = [];
      originalValues.forEach((originalText, textNode) => {
        if (textNode.parentNode) {
          textNode.textContent = originalText;
        } else {
          staleNodes.push(textNode);
        }
      });
      staleNodes.forEach(node => originalValues.delete(node));
    }

    // Restore split price pairs and drop stale entries from previous pages
    restoreSplitPrices();
    splitPricePairs = splitPricePairs.filter(
      p => p.symbolElement.parentNode && p.amountElement.parentNode
    );
    restoreAmountBeforeSymbol();
    amountSymbolPairs = amountSymbolPairs.filter(
      p => p.amountElement.parentNode && p.symbolTextNode.parentNode
    );

    currentCurrency = targetCurrency;

    // Nothing to convert when reverting to the source currency (EUR)
    if (targetCurrency !== DEFAULT_CURRENCY) {
      processCurrencyRoot(root, targetCurrency);
    }

    // Save preference
    saveCurrencyPreference(targetCurrency);
    dispatchCurrencyChangeEvent(targetCurrency);
  } finally {
    hasAppliedCurrency = true;
    markCurrencyReady(targetCurrency);
    observeCurrencyMutations();
  }
}

/**
 * Dispatch a custom event when currency changes
 * @param {string} currency - The new currency code
 */
function dispatchCurrencyChangeEvent(currency) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('currencyChange', { 
      detail: { currency, config: CURRENCY_CONFIG[currency] } 
    }));
  }
}

/**
 * Get the current currency
 * @returns {string} - Current currency code
 */
export function getCurrentCurrency() {
  return currentCurrency;
}

/**
 * Get the default currency
 * @returns {string} - Default currency code
 */
export function getDefaultCurrency() {
  return DEFAULT_CURRENCY;
}

/**
 * Get all supported currencies
 * @returns {Object} - Currency configurations
 */
export function getSupportedCurrencies() {
  return { ...CURRENCY_CONFIG };
}

/**
 * Read a supported currency from a URL query string.
 * Currency codes are matched case-insensitively (for example, ?currency=bdt).
 *
 * @param {string} [search=window.location.search] - URL query string
 * @returns {string|null} - Normalized currency code, or null when unsupported/missing
 */
export function getCurrencyFromQuery(search) {
  if (typeof search !== 'string') {
    if (typeof window === 'undefined') return null;
    search = window.location.search;
  }

  const currency = new URLSearchParams(search).get('currency');
  if (!currency) return null;

  const normalizedCurrency = currency.trim().toUpperCase();
  return CURRENCY_CONFIG[normalizedCurrency] ? normalizedCurrency : null;
}

/**
 * Detect a supported currency from the browser's system timezone and locale.
 * This does not request geolocation permission or call an external service.
 *
 * Optional values make the detector deterministic for tests and non-browser use.
 * @param {Object} [options]
 * @param {string} [options.timeZone]
 * @param {string[]} [options.locales]
 * @returns {string|null}
 */
export function detectCurrencyFromBrowser(options = {}) {
  let timeZone = options.timeZone;
  let locales = options.locales;

  if (!timeZone) {
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_error) {
      timeZone = '';
    }
  }

  if (TIME_ZONE_CURRENCY_MAP[timeZone]) {
    return TIME_ZONE_CURRENCY_MAP[timeZone];
  }

  if (timeZone?.startsWith('Australia/')) return 'AUD';

  // Unsupported European currencies fall back to EUR instead of accidentally
  // selecting USD from a common en-US browser-language setting.
  if (timeZone?.startsWith('Europe/')) return 'EUR';

  if (!locales && typeof navigator !== 'undefined') {
    locales = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
  }

  for (const locale of locales || []) {
    if (!locale) continue;

    let region = '';
    try {
      region = new Intl.Locale(locale).region || '';
    } catch (_error) {
      const match = String(locale).match(/[-_]([A-Za-z]{2}|\d{3})(?:[-_]|$)/);
      region = match?.[1] || '';
    }

    const detectedCurrency = COUNTRY_CURRENCY_MAP[region.toUpperCase()];
    if (detectedCurrency) return detectedCurrency;
  }

  return null;
}

/**
 * Get exchange rates
 * @returns {Object} - Exchange rates (relative to EUR)
 */
export function getExchangeRates() {
  return { ...EXCHANGE_RATES };
}

/**
 * Update exchange rate for a specific currency
 * @param {string} currencyCode - The currency code
 * @param {number} rate - The new rate (relative to EUR)
 */
export function updateExchangeRate(currencyCode, rate) {
  if (EXCHANGE_RATES.hasOwnProperty(currencyCode)) {
    EXCHANGE_RATES[currencyCode] = rate;
  }
}

/**
 * Save currency preference to localStorage
 * @param {string} currency - Currency code to save
 */
function saveCurrencyPreference(currency) {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('preferred_currency', currency);
  }
}

/**
 * Load currency preference from localStorage
 * @returns {string} - Saved currency code or default (EUR)
 */
export function loadCurrencyPreference() {
  if (typeof localStorage !== 'undefined') {
    const savedCurrency = localStorage.getItem('preferred_currency');
    const normalizedCurrency = savedCurrency?.trim().toUpperCase();

    if (normalizedCurrency && CURRENCY_CONFIG[normalizedCurrency]) {
      return normalizedCurrency;
    }
  }
  return DEFAULT_CURRENCY;
}

/**
 * Resolve the active currency in priority order:
 * URL override, saved user preference, browser location, then EUR.
 *
 * @param {string} [search=window.location.search] - URL query string
 * @returns {string}
 */
export function getPreferredCurrency(search) {
  const queryCurrency = getCurrencyFromQuery(search);
  if (queryCurrency) return queryCurrency;

  if (typeof localStorage !== 'undefined') {
    const savedCurrency = localStorage.getItem('preferred_currency');
    const normalizedCurrency = savedCurrency?.trim().toUpperCase();

    if (normalizedCurrency && CURRENCY_CONFIG[normalizedCurrency]) {
      return normalizedCurrency;
    }
  }

  if (
    typeof window !== 'undefined' &&
    CURRENCY_CONFIG[window.__INITIAL_CURRENCY__]
  ) {
    return window.__INITIAL_CURRENCY__;
  }

  return detectCurrencyFromBrowser() || DEFAULT_CURRENCY;
}

/**
 * Initialize the currency changer
 * - Resolves URL, saved, or browser-location currency
 * - Sets up mutation observer for dynamic content
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoConvert=true] - Auto-convert on init
 * @param {boolean} [options.observeMutations=true] - Watch for DOM changes
 */
export function initCurrencyChanger(options = {}) {
  if (typeof window === 'undefined') return;
  if (isInitialized) return;
  
  const {
    autoConvert = true,
    observeMutations = true,
  } = options;
  
  isInitialized = true;
  
  // URL and saved choices take precedence over browser-location detection.
  const queryCurrency = getCurrencyFromQuery();
  const preferredCurrency = getPreferredCurrency();
  currentCurrency = preferredCurrency;
  
  // An explicit query parameter is always applied. This also handles
  // ?currency=eur when a different currency was previously saved.
  if (autoConvert && (queryCurrency || preferredCurrency !== DEFAULT_CURRENCY)) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        changeCurrency(preferredCurrency);
      });
    } else {
      changeCurrency(preferredCurrency);
    }
  }
  
  // Set up mutation observer for dynamic content
  if (observeMutations) {
    const pendingRoots = new Set();
    let frameId = null;

    const flushPendingRoots = () => {
      frameId = null;
      if (!hasAppliedCurrency || currentCurrency === DEFAULT_CURRENCY) {
        pendingRoots.clear();
        return;
      }

      const roots = Array.from(pendingRoots).filter((root) => root.isConnected);
      pendingRoots.clear();

      // If an ancestor is already queued, processing its descendants again is
      // redundant and was the main source of startup main-thread blocking.
      const topLevelRoots = roots.filter(
        (root) => !roots.some((other) => other !== root && other.contains(root))
      );
      currencyMutationObserver?.disconnect();
      topLevelRoots.forEach((root) => processCurrencyRoot(root, currentCurrency));
      observeCurrencyMutations();
    };

    currencyMutationObserver = new MutationObserver((mutations) => {
      if (!hasAppliedCurrency || currentCurrency === DEFAULT_CURRENCY) return;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Skip currency selector
            if (node.id === 'currency-selector' || node.closest?.('#currency-selector')) return;
            pendingRoots.add(node);
          } else if (node.nodeType === Node.TEXT_NODE) {
            if (node.parentElement && !node.parentElement.closest('#currency-selector')) {
              pendingRoots.add(node.parentElement);
            }
          }
        });
      });

      if (pendingRoots.size > 0 && frameId === null) {
        frameId = requestAnimationFrame(flushPendingRoots);
      }
    });
    
    observeCurrencyMutations();
  }
}

/**
 * Reset to default currency (EUR)
 */
export function resetCurrency() {
  changeCurrency(DEFAULT_CURRENCY);
}

/**
 * Manually convert a single value
 * @param {number} amount - The amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {string} - Formatted converted amount
 */
export function convertAndFormat(amount, fromCurrency, toCurrency) {
  const converted = convertAmount(amount, fromCurrency, toCurrency);
  return formatCurrency(converted, toCurrency);
}

// Export for use in browser console
if (typeof window !== 'undefined') {
  window.currencyChanger = {
    changeCurrency,
    resetCurrency,
    getCurrentCurrency,
    getDefaultCurrency,
    getSupportedCurrencies,
    getExchangeRates,
    updateExchangeRate,
    initCurrencyChanger,
    convertAndFormat,
    loadCurrencyPreference,
    getCurrencyFromQuery,
    detectCurrencyFromBrowser,
    getPreferredCurrency,
    markCurrencyReady,
  };
}

const currencyChangerModule = {
  changeCurrency,
  resetCurrency,
  getCurrentCurrency,
  getDefaultCurrency,
  getSupportedCurrencies,
  getExchangeRates,
  updateExchangeRate,
  initCurrencyChanger,
  convertAndFormat,
  loadCurrencyPreference,
  getCurrencyFromQuery,
  detectCurrencyFromBrowser,
  getPreferredCurrency,
  markCurrencyReady,
  DEFAULT_CURRENCY,
};

export default currencyChangerModule;
